/**
 * Sperrzeiten im Planer anzeigen (16.10.2026):
 * - SperrZeilen: schmale graue Zeilen ganz oben im Tag — „Volker nicht da · ganztägig".
 * - SperrStreifen: dünner Streifen unten in der Monatszelle mit dem Anfangsbuchstaben.
 *   Ganztags füllt er die Breite, ein paar Stunden nur einen Teil.
 * Sperren sind keine Termine — deshalb grau und schraffiert statt farbig.
 * Bürotage (20.10.2026) stehen als eine helle Zeile „Bürotag · Benjamin & Volker"
 * darüber — für beide eingetragen, aber als einer gezeigt.
 */
import {
  bueroGruppen, ganzerTag, istBuero, kuerzel, namenListe, vorname, zeitAmTag, type Sperre,
} from "../../hooks/useSperrzeiten";
import "../../styles/sperrzeit.css";

export function SperrZeilen({ tag, sperren, onSperre }: {
  tag: string; sperren: Sperre[]; onSperre: (s: Sperre) => void;
}) {
  if (sperren.length === 0) return null;
  return (
    <div className="sz-zeilen">
      {bueroGruppen(sperren).map((g) => (
        <button key={g[0].schluessel} type="button" className="sz-zeile sz-buero" onClick={() => onSperre(g[0])}>
          <span className="sz-kreis" aria-hidden="true">☕</span>
          <span className="sz-zeile-text">
            <strong>Bürotag</strong>
            <span> · {namenListe(g)} · {zeitAmTag(g[0], tag)}{g[0].notiz ? ` · ${g[0].notiz}` : ""}</span>
          </span>
        </button>
      ))}
      {sperren.filter((s) => !istBuero(s)).map((s) => (
        <button key={s.schluessel} type="button" className="sz-zeile" onClick={() => onSperre(s)}>
          <span className="sz-kreis" aria-hidden="true">{kuerzel(s.person_name)}</span>
          <span className="sz-zeile-text">
            <strong>{vorname(s.person_name)} nicht da</strong>
            <span> · {zeitAmTag(s, tag)}{s.notiz ? ` · ${s.notiz}` : ""}{s.quelle === "kalender" ? " · Kalender" : ""}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Tagesblatt am Rechner: „Bürotag" und „Nicht da" oben rechts, darunter die Sperren des Tages. */
export function SperrTag({ tag, sperren, onSperre, onNichtDa, onBuerotag }: {
  tag: string; sperren: Sperre[]; onSperre: (s: Sperre) => void; onNichtDa: () => void; onBuerotag?: () => void;
}) {
  return (
    <>
      <div className="sz-tagesblatt">
        {onBuerotag && <BuerotagKnopf onClick={onBuerotag} />}
        <NichtDaKnopf onClick={onNichtDa} />
      </div>
      <SperrZeilen tag={tag} sperren={sperren} onSperre={onSperre} />
    </>
  );
}

/** Knopf „Nicht da" neben „＋ Termin". */
export function NichtDaKnopf({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="sz-nichtda" onClick={onClick}>
      Nicht da
    </button>
  );
}

/** Knopf „Bürotag" (20.10.2026) — still wie „Nicht da". */
export function BuerotagKnopf({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="sz-nichtda sz-buerotag" onClick={onClick}>
      Bürotag
    </button>
  );
}

export function SperrStreifen({ tag, sperren }: { tag: string; sperren: Sperre[] }) {
  if (sperren.length === 0) return null;
  /* Eine Person, ein Streifen — auch wenn sie an dem Tag zweimal gesperrt ist. */
  const jePerson = new Map<string, Sperre[]>();
  for (const s of sperren.filter((x) => !istBuero(x))) jePerson.set(s.person_id, [...(jePerson.get(s.person_id) ?? []), s]);
  return (
    <div className="sz-streifen">
      {bueroGruppen(sperren).map((g) => {
        const voll = ganzerTag(g[0], tag);
        const text = `Bürotag · ${namenListe(g)} · ${zeitAmTag(g[0], tag)}`;
        return (
          <span key={g[0].schluessel} className={"sz-band sz-band-buero" + (voll ? " sz-band-voll" : "")} title={text} aria-label={text}>
            <span className="sz-band-k">Büro</span>
            <span className="sz-band-name">Bürotag</span>
          </span>
        );
      })}
      {[...jePerson.values()].map((liste) => {
        const voll = liste.some((s) => ganzerTag(s, tag));
        const name = vorname(liste[0].person_name);
        const text = `${name} nicht da · ${liste.map((s) => zeitAmTag(s, tag)).join(", ")}`;
        return (
          <span key={liste[0].person_id} className={"sz-band" + (voll ? " sz-band-voll" : "")} title={text} aria-label={text}>
            <span className="sz-band-k">{kuerzel(liste[0].person_name)}</span>
            {voll
              ? <span className="sz-band-name">{name}</span>
              : <span className="sz-band-zeit">{liste.map((s) => zeitAmTag(s, tag)).join(", ")}</span>}
          </span>
        );
      })}
    </div>
  );
}
